import { useIntl } from "react-intl";

import { ReactComponent as CheckCircle } from "assets/images/iconsV2/check-circle.svg";
import Badge from "components/v2/Badge/Badge";
import Avatar from "components/v2/Avatar/Avatar";
import { classNames } from "helpers/commonHelpers";

import { DetailsSheetSection } from "../DetailsSheetSection";
import { SHIPMENT_DETAILS_TEST_IDS, shipmentDetailsTestId } from "../testIds";
import type { InvitedProviderCard } from "../types";

type ProvidersSectionProps = {
  providers: InvitedProviderCard[];
};

export function ProvidersSection({ providers }: Readonly<ProvidersSectionProps>) {
  const intl = useIntl();

  return (
    <DetailsSheetSection
      testId={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_SECTION_PROVIDERS}
      title={intl.formatMessage({ id: "shipment-details.providers", defaultMessage: "Providers" })}
    >
      <div
        className="shipment-details-sheet__provider-columns"
        data-testid={SHIPMENT_DETAILS_TEST_IDS.SHIPMENT_DETAILS_PROVIDERS_LIST}
      >
        {providers.map((p) => {
          const base = shipmentDetailsTestId.providerCard(p.id);
          const isAvatarTopAligned = Boolean(p.companyName && p.teamName && p.parcelAccountName);

          return (
            <div
              key={p.id}
              className={classNames(
                "rfv2-review__provider-cell",
                "shipment-details-sheet__provider-cell",
                { "shipment-details-sheet__provider-cell--avatar-top-aligned": isAvatarTopAligned },
              )}
              data-testid={base}
            >
              <span className="shipment-details-sheet__provider-avatar" data-testid={`${base}-avatar`}>
                <Avatar size="sm" image={p.companyLogo || undefined} name={p.companyName} />
              </span>
              <div className="rfv2-review__provider-info">
                <div className="shipment-details-sheet__provider-identity">
                  <span className="rfv2-review__provider-name" data-testid={`${base}-name`}>
                    {p.companyName}
                  </span>
                  {p.teamName && (
                    <span className="rfv2-review__provider-team" data-testid={`${base}-team`}>
                      {p.teamName}
                    </span>
                  )}
                  {p.parcelAccountName && (
                    <span className="rfv2-review__provider-team" data-testid={`${base}-parcel-account`}>
                      {p.parcelAccountName}
                      {p.parcelAccountNumber ? ` - ${p.parcelAccountNumber}` : ""}
                    </span>
                  )}
                </div>
              </div>
              {p.won && (
                <span className="shipment-details-sheet__provider-won" data-testid={`${base}-won`}>
                  <Badge
                    type="status-badge"
                    color="success"
                    variant="icon-leading"
                    size="sm"
                    icon={<CheckCircle className="icon" aria-hidden="true" />}
                    label={intl.formatMessage({
                      id: "shipment-quotes.components.invitedProviders.won",
                      defaultMessage: "Won",
                    })}
                  />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </DetailsSheetSection>
  );
}
